/**
 * learn-more.ts — educational drop-down copy per command.
 *
 * Phase CI.4. The CLI is the framework's no-AI proof: anyone can compute
 * and learn just by typing. Every key command card carries a `[?] learn
 * more` toggle that expands to four panels:
 *   • what — the one-line definition
 *   • why  — why this exists in Zeq
 *   • composes — how it relates to neighbouring pieces
 *   • try  — 2-3 runnable examples
 * Plus optional `related` (cross-links) and `contractAction` (the "push
 * this query into a contract" close-the-loop hint).
 *
 * Copy is sourced directly from the framework's locked vocabulary and the
 * 7-step protocol — no marketing voice. The reader should be able to learn
 * the whole language by walking from one entry to the next.
 */

export interface LearnMore {
  /** One-line definition. Plain English, no jargon up-front. */
  what: string;
  /** Why this exists in Zeq — the architectural intent. */
  why: string;
  /** How this composes with other operators / commands. */
  composes: string;
  /** 2-3 runnable examples. Each must be valid CLI input. */
  tryThis: string[];
  /** Optional related command heads — for cross-linking from the toggle. */
  related?: string[];
  /** Optional close-the-loop hint: "push this into a contract". */
  contractAction?: { label: string; intent: string };
}

/** The full educational library — keyed by top-level command head. */
export const LEARN_MORE: Record<string, LearnMore> = {

  compute: {
    what: "Run any of the 1,500+ verified kernel operators and get back a signed CKO envelope — the framework's tamper-evident proof of compute.",
    why: "ZeqVM is a virtual machine where physical laws are the instruction set. Every compute() call applies KO42 (the metric tensioner) as a mandatory carrier, then 1-3 additional operators per the 7-step protocol — the result is bit-exact reproducible to ≤0.1% against textbook physics.",
    composes: "compute is the engine. operators discovers what to run. families groups them into 8 taxonomic buckets. the entangled state records every transition. credits fund the call; each compute mints a ZEQ envelope. contract turns repeated computes into a state machine.",
    tryThis: [
      "compute NM19 mass=5 acceleration=2",
      "compute QM10 frequency=5e14",
      "compute GR37 mass=1.989e30",
    ],
    related: ["operators", "families", "state", "credits", "contract"],
    contractAction: {
      label: "Push this compute into a contract template",
      intent: "Turn one-shot computation into a recurring state-machine transition — fires on a schedule, on an event, or on cron. The contract logs every fire to the entangled state.",
    },
  },

  operators: {
    what: "The 1,500+-operator catalogue across 67 domains. Every operator has a verbatim formula, named role in the HULYAS Master Equation, and a runnable binding through /api/playground/compute.",
    why: "Physics is a library; Zeq is the index. Operators are addressable by ID (NM19, QM10, GR37, KO42, etc.), grouped by domain (Newtonian Mechanics, Quantum Mechanics, …) and family (Kinematic, Awareness, Hulyas Forensics, …). KO42 is mandatory on every compute call; additional operators capped at 3 (total ≤4 per 7-step protocol rule 2).",
    composes: "operators is discovery. compute is execution. families is taxonomy. The IDs from operators search feed directly into compute calls. The formulas are real (no hand-waving).",
    tryThis: [
      "operators count",
      "operators search schrodinger",
      "operators list Newtonian Mechanics",
      "operators show NM19",
    ],
    related: ["compute", "families", "docs", "api"],
  },

  api: {
    what: "The 138-endpoint public HTTP surface of every Zeq deployment, grouped into 31 thematic groups (Chain, Contracts, Tally, HITE, Kernel, LLM Gateway, …). Every response is phase-stamped to the 1.287 Hz HulyaPulse.",
    why: "Self-hostable means: everything Zeq does over the wire is documented, callable, and reproducible against your own ZeqVM. No hidden state, no proprietary gates. The api command lets you browse the surface from inside the terminal, then jump to the SDK page for the contract.",
    composes: "Each api group maps 1:1 to a folder in app/artifacts/api-server/src/routes/. The CLI's other commands (state, contract, credits, compute, oracle) are thin wrappers over those HTTP routes — the api command shows you what's actually available so you can call routes the CLI doesn't yet wrap.",
    tryThis: [
      "api",
      "api show chain",
      "api search audit",
      "api total",
    ],
    related: ["apis", "skill", "docs"],
  },

  protocols: {
    what: "236 protocols organised into 9 themes (Time & Coordination, Identity & Access, Key & Custody, Encryption, Transport & Messaging, Computation & Compile, Consensus & Ledger, Measurement & Observability, Governance & Policy) and ~50 domains (Agriculture → Weather).",
    why: "Protocols are how the kernel speaks — across time, keys, bytes, and witnesses. Each protocol is a composition of operators with a concrete outcome. KO42 anchors them all; ZTB1 bridges Zeqond ↔ Unix; CS87 (Kolmogorov complexity) anchors identity and custody.",
    composes: "A protocol composes 2-7 operators into a named pattern (rendezvous, sibling-tether, TESC frame, truth-DAG block, …). The theme is the why; the operators are the how; the domain is the where it fits.",
    tryThis: [
      "protocols",
      "protocols domains",
      "protocols show 2",
      "protocols search consensus",
    ],
    related: ["operators", "families", "docs", "api"],
  },

  families: {
    what: "The 8-family taxonomy of every operator in the kernel: Kinematic Spectrum, Quantum Mechanics, Newtonian Mechanics, General Relativity, Computer Science, Awareness, Hulyas Forensics, Agentic Operations, Zeq Resonance.",
    why: "Families group operators by epistemic kind — wave mechanics vs. classical motion vs. spacetime curvature vs. complexity theory vs. observation/intent. The Kinematic Spectrum is the bootstrap surface (42+ core operators); the other families pull from the full 1,500+-operator catalogue when domain depth is required.",
    composes: "Pick a family → list its operators → compute one. Awareness operators (ON0, VX, ZEQ00, ZEQ-PROTECT, ZEQ-TETHER, ZEQ-POCKET) and Resonance operators (ZRO00, ZRO149, RHY1-RHY4) are the framework's distinctive contribution — formulas you won't find in textbooks.",
    tryThis: [
      "families",
      "families show quantum-mechanics",
      "families show awareness",
    ],
    related: ["operators", "compute", "protocols"],
  },

  skill: {
    what: "The Kernel Skill (zeq-os-mi-kernel) — the framework's boot sequence as a SKILL.md any LLM client (Claude Code, Cowork, Cursor, OpenAI Assistants) can load as a system prompt.",
    why: "Three canonical endpoints (prompt, skill.md, manifest) keep every install in sync from one source of truth. The manifest carries a sha256 so any installed copy can be verified against the live one — no drift, no forks, no \"my Zeq is different from yours\".",
    composes: "skill md is the markdown bytes; skill prompt is the boot-prompt slice; skill manifest is the JSON descriptor with version + sha256. skill install <client> prints the install snippet for the four supported clients.",
    tryThis: [
      "skill",
      "skill manifest",
      "skill install claude-code",
      "skill verify",
    ],
    related: ["docs", "api", "operators"],
  },

  state: {
    what: "The entangled-state log on this machine — every transition appended, hash-linked to the prior, tamper-evident. Each row carries a zeqond tick, prior-hash, operator IDs, and ZeqProof.",
    why: "Each transition's hash includes the prior, so rewriting any past row breaks every row after it. The entangled state is your machine's complete, tamper-evident activity ledger.",
    composes: "compute writes one entangled-state row per call. contract writes one row per transition. credits swaps and oracle ticks also append. state audit shows recent rows; state explore opens the visual /state/?slug= explorer.",
    tryThis: [
      "state head",
      "state audit --limit 10",
      "state explore",
    ],
    related: ["compute", "contract", "credits", "oracle"],
  },

  contract: {
    what: "A JSON state machine that fires transitions on a schedule, on an event, on a cron, or on a logic tree (any/all/within/aggregate). Every fire writes a row to the entangled state and can call external APIs.",
    why: "Contracts are how compute becomes a system. Instead of running one operator manually, you describe \"every Zeqond / every event matching X / every business-hours cron\" and the scheduler fires it. Pre-actions call external APIs; post-actions branch state or emit events that other contracts listen to.",
    composes: "compute is the operator. apis is the external surface a contract can call. the entangled state is where every fire is logged. agents can spawn contracts. The Queries IDE (Queries tab) is the visual editor; this CLI is the verb surface.",
    tryThis: [
      "contract list",
      "contract show ct_…",
      "contract dry-run ct_…",
      "contract fire-now ct_…",
    ],
    related: ["compute", "apis", "state", "agents"],
  },

  credits: {
    what: "Your credits and ZEQ envelopes. Credits are fuel — 1,287 free on signup, 777 more by daily claim, never transferable. Every compute they fund mints a ZEQ envelope, the transferable token, worth more the better your precision (V = cost × (2 − precision/0.001)).",
    why: "Compute has a real cost — server CPU, the ≤0.1% precision gate, the entangled-state write. The envelope model ties value to verified work: paid-funded computes split 80/20 between you and the Foundation; free-funded mint 100% to the Foundation. No passive mint, no passive burn — value moves only when you compute.",
    composes: "compute spends credits and mints envelopes. credits shows your credits, envelopes, and the recent ledger. credits swap converts BTC/ETH → ZEQ via the foundation pot. oracle shows network-wide supply state.",
    tryThis: [
      "credits",
      "credits swap btc 0.001",
      "credits tally",
    ],
    related: ["compute", "oracle", "contract"],
  },

  oracle: {
    what: "The Transparency Oracle — the per-Zeqond network supply state. Shows total ZEQ in circulation, foundation pot, live BTC/ETH price, and active machine count.",
    why: "Public, append-only, queryable. Anyone can audit the supply curve, the foundation reserves, and the swap rate without trusting Zeq's word. The oracle is itself a state machine that ticks on every Zeqond.",
    composes: "oracle now is a single snapshot. oracle history N walks the last N Zeqonds of supply state. Every credits swap appears on the oracle; every compute charge appears here aggregated.",
    tryThis: [
      "oracle",
      "oracle history 10",
    ],
    related: ["credits", "state", "docs transparency"],
  },

  pulse: {
    what: "The 1.287 Hz HulyaPulse — the framework's native clock. One Zeqond = 0.777 s (= 1/1.287). Every Zeq surface is phase-locked to this tick.",
    why: "Derived from f = c / λ_φ where λ_φ = 2π r_φ; not arbitrary. The Pulse is what makes every reproducibility claim provable: \"same input, same Zeqond, same hash\" is enforceable because both ends know the canonical clock.",
    composes: "pulse status shows current Zeqond + phase + R_t. pulse clock streams the live tick. time shows Zeqond / Unix epoch / local. Every operator call records the Zeqond it fired on.",
    tryThis: [
      "pulse",
      "pulse status",
      "time",
    ],
    related: ["state", "oracle", "docs"],
  },

  agents: {
    what: "LLM hyper-agents that you spawn with a directive (\"find the lowest-energy orbit\"). The agent gets its own ZID, its own entangled-state entries, and ticks autonomously — synthesising skills, calling operators, promoting derived operators to the public catalogue.",
    why: "Sometimes one compute is enough. Sometimes you need a recursive search across the 1,500+-operator surface. Agents wrap a 5-step protocol: define → synthesise skills → derive operators → tick → fitness-check against ZeqProof.",
    composes: "agents spawn creates one. agents tick advances it manually. agents mesh shows its links to other agents. Skills it synthesises feed back into the skills catalogue. Every action writes to the entangled state.",
    tryThis: [
      "agents",
      "agents spawn \"find the lowest-energy orbit for a small planet\"",
      "agents mesh zag_…",
    ],
    related: ["skills", "compute", "state", "contract"],
  },

  skills: {
    what: "Procedural skills synthesised by the framework from natural-language intent (\"compute orbital velocity for any planet\"). Each skill is a saved composition of operators with named inputs.",
    why: "Skills are the bridge between \"one compute call\" and \"a permanent recipe\". They survive across sessions, can be called by agents, and can be promoted to the public catalogue if their ZeqProof fitness clears the bar.",
    composes: "skills synthesise creates one from an intent string. skills list shows what's on this machine. Agents synthesise skills automatically during their tick loop. Every synthesis is complexity-priced in credits.",
    tryThis: [
      "skills",
      "skills synthesise \"compute orbital velocity for any planet\"",
    ],
    related: ["agents", "compute", "operators"],
  },

  apis: {
    what: "External APIs you've registered on this machine — name + URL + auth headers. Contracts can call them as pre-actions before computing, and as post-actions after committing.",
    why: "The framework runs in isolation by default. APIs is how you let one specific contract reach out to one specific endpoint (weather.gov, your own data lake, a webhook target) — all with the per-API auth held in the encrypted store, never inline.",
    composes: "apis add registers one. Contracts reference it by ID in their pre_actions block. The L+M scheduler runs pre_actions → compute → post_actions in order. Every external call is logged to the entangled state with redacted secrets.",
    tryThis: [
      "apis",
      "apis add weather https://api.weather.gov/...",
      "apis test api_…",
    ],
    related: ["contract", "state", "api"],
  },

  docs: {
    what: "Opens the relevant SDK page in a new tab — 23 known topics including api, operators, protocols, kernel-skill, agents, byok, transparency, credits, etc.",
    why: "The terminal is the verb surface; the SDK is the prose. When you've discovered something here and want the full explanation with diagrams and code samples, docs <topic> jumps you straight there.",
    composes: "Every CLI card has a docs hint in its footer. docs bare lists every known topic. Unknown topics fall back to a search page so nothing dead-ends.",
    tryThis: [
      "docs",
      "docs operators",
      "docs kernel-skill",
    ],
    related: ["api", "skill", "help"],
  },
};

/** Render a learn-more block as HTML. Returns "" if no entry for this command. */
export function renderLearnMore(commandHead: string): string {
  const lm = LEARN_MORE[commandHead.toLowerCase()];
  if (!lm) return "";
  const tryRows = lm.tryThis
    .map(t => '<div class="cli-lm-try-row"><code>' + escHtml(t) + '</code></div>')
    .join("");
  const related = (lm.related && lm.related.length)
    ? '<div class="cli-lm-block"><span class="cli-lm-label">related</span>' +
      '<span class="cli-lm-related">' + lm.related.map(r => '<code>' + escHtml(r) + '</code>').join(" · ") + '</span></div>'
    : "";
  const contractCta = lm.contractAction
    ? '<div class="cli-lm-block cli-lm-contract">' +
        '<span class="cli-lm-label">↳ next</span>' +
        '<span class="cli-lm-contract-body">' +
          '<strong>' + escHtml(lm.contractAction.label) + '</strong>' +
          '<div class="cli-lm-contract-intent">' + escHtml(lm.contractAction.intent) + '</div>' +
        '</span></div>'
    : "";
  return (
    '<details class="cli-lm">' +
      '<summary class="cli-lm-summary">' +
        '<span class="cli-lm-icon">?</span>' +
        '<span class="cli-lm-summary-text">learn more</span>' +
      '</summary>' +
      '<div class="cli-lm-body">' +
        '<div class="cli-lm-block"><span class="cli-lm-label">what</span><span>' + escHtml(lm.what) + '</span></div>' +
        '<div class="cli-lm-block"><span class="cli-lm-label">why</span><span>' + escHtml(lm.why) + '</span></div>' +
        '<div class="cli-lm-block"><span class="cli-lm-label">composes</span><span>' + escHtml(lm.composes) + '</span></div>' +
        '<div class="cli-lm-block"><span class="cli-lm-label">try</span><div class="cli-lm-try">' + tryRows + '</div></div>' +
        related +
        contractCta +
      '</div>' +
    '</details>'
  );
}

/** List every command head that has a learn-more entry — for the help footer. */
export function learnMoreKeys(): string[] {
  return Object.keys(LEARN_MORE).sort();
}

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
